import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Espaço reservado além da largura do afixo, em pixels.
 *
 * São duas parcelas: os 12px em que o afixo já está deslocado da borda
 * (`left-3`/`right-3`) e mais 8px de respiro entre ele e o texto. Sem a
 * segunda, o valor encosta no rótulo e "30dias" vira uma palavra só.
 */
const DESLOCAMENTO_DO_AFIXO = 12;
const RESPIRO_ATE_O_TEXTO = 8;
const FOLGA_DO_AFIXO = DESLOCAMENTO_DO_AFIXO + RESPIRO_ATE_O_TEXTO;

/**
 * Mede o prefixo/sufixo de um campo e devolve o espaço que ele precisa reservar.
 *
 * Usado pelo `TextField` e pelo `NumberField`. Espaço fixo (`pl-10`/`pr-10`)
 * bastava para "R$" e "%", mas não para "msg/dia" nem "/blog/cluster/": o valor
 * digitado passava por baixo do afixo e os dois saíam impressos um sobre o
 * outro, ilegíveis. Medir resolve para qualquer afixo, hoje e depois.
 *
 * `espaco` é `undefined` enquanto não há medida (primeiro render, ambiente sem
 * layout como o jsdom) — aí vale o espaço fixo da classe do campo.
 */
export const useLarguraDoAfixo = (afixo: ReactNode) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [largura, setLargura] = useState(0);

  // `useLayoutEffect` para a medida entrar antes da pintura: com `useEffect` o
  // primeiro quadro sai com o texto sobreposto.
  useLayoutEffect(() => {
    if (!afixo) {
      setLargura(0);
      return;
    }
    const elemento = ref.current;
    if (!elemento) return;

    const medir = () => setLargura(elemento.getBoundingClientRect().width);
    medir();

    // A largura muda com a fonte carregada e com o zoom do navegador.
    if (typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    return () => observador.disconnect();
  }, [afixo]);

  // A fonte da marca é self-hosted: até ela chegar, a medida sai com a fonte
  // de sistema e fica curta demais.
  useEffect(() => {
    if (!afixo || !document.fonts?.ready) return;
    let vivo = true;
    document.fonts.ready.then(() => {
      if (vivo && ref.current) {
        setLargura(ref.current.getBoundingClientRect().width);
      }
    });
    return () => {
      vivo = false;
    };
  }, [afixo]);

  return { ref, espaco: largura > 0 ? largura + FOLGA_DO_AFIXO : undefined };
};
